import { useState } from "react";
import { Link } from "react-router-dom";
import { communityFeed, artists } from "@/mocks/artistData";
import Navbar from "@/pages/home/components/Navbar";

type FeedItem = typeof communityFeed[0];

const sortTabs = ["최신순", "인기순", "팔로잉"];
const filterDurations = ["전체", "15초 이하", "30초 이하", "30초 이상"];

export default function CommunityPage() {
  const [activeSort, setActiveSort] = useState("최신순");
  const [selectedArtist, setSelectedArtist] = useState("all");
  const [selectedDuration, setSelectedDuration] = useState("전체");
  const [feedItems, setFeedItems] = useState(communityFeed);

  const toggleLike = (id: string) => {
    setFeedItems((prev) =>
      prev.map((item) =>
        item.id === id
          ? { ...item, liked: !item.liked, likes: item.liked ? item.likes - 1 : item.likes + 1 }
          : item
      )
    );
  };

  const toggleBookmark = (id: string) => {
    setFeedItems((prev) =>
      prev.map((item) => (item.id === id ? { ...item, bookmarked: !item.bookmarked } : item))
    );
  };

  const filtered = feedItems
    .filter((item) => selectedArtist === "all" || item.artistId === selectedArtist)
    .sort((a, b) => {
      if (activeSort === "인기순") return b.likes - a.likes;
      return 0;
    });

  const totalLikes = feedItems.reduce((s, i) => s + i.likes, 0);

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white">
      <Navbar />

      {/* Hero */}
      <div className="relative overflow-hidden pt-24 pb-16">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/f1b47cd9f95a0453679e4f3a6db55bd8.jpg"
            alt="Community Hero"
            className="w-full h-full object-cover object-top opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f]/70 via-[#0a0a0f]/50 to-[#0a0a0f]" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-2 text-sm text-white/40 mb-6">
            <Link to="/" className="hover:text-white/70 transition-colors cursor-pointer">홈</Link>
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-s-line text-xs" />
            </div>
            <span className="text-white/70">커뮤니티</span>
          </div>
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#4cc9f0]/20 to-[#f72585]/20 border border-white/10">
              <i className="ri-community-line text-xl text-[#4cc9f0]" />
            </div>
            <span className="text-[#4cc9f0] text-sm font-semibold tracking-widest uppercase">Community Feed</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-black text-white leading-tight mb-3">
            팬들의
            <span className="bg-gradient-to-r from-[#4cc9f0] via-[#f72585] to-[#ffd60a] bg-clip-text text-transparent ml-3">
              AI 영상 피드
            </span>
          </h1>
          <p className="text-white/50 text-base max-w-xl">
            아티스트 퀘스트를 완료한 팬들의 AI 영상 작품을 감상하고 소통하세요.
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 pb-24 space-y-8">
        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "총 작품", value: `${feedItems.length}개`, icon: "ri-video-line", color: "#4cc9f0" },
            { label: "총 좋아요", value: `${(totalLikes / 1000).toFixed(1)}K`, icon: "ri-heart-line", color: "#f72585" },
            { label: "총 댓글", value: `${feedItems.reduce((s, i) => s + i.comments, 0)}개`, icon: "ri-chat-1-line", color: "#ffd60a" },
            { label: "크리에이터", value: `${feedItems.length}명`, icon: "ri-user-line", color: "#00f5d4" },
          ].map((stat) => (
            <div key={stat.label} className="rounded-2xl border border-white/10 bg-white/5 p-5 flex items-center gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-xl flex-shrink-0" style={{ background: `${stat.color}18`, border: `1px solid ${stat.color}40` }}>
                <i className={`${stat.icon} text-lg`} style={{ color: stat.color }} />
              </div>
              <div>
                <div className="text-white font-black text-xl">{stat.value}</div>
                <div className="text-white/40 text-xs">{stat.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          {/* Sort Tabs */}
          <div className="flex items-center gap-1 p-1 rounded-full bg-white/5 border border-white/10">
            {sortTabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveSort(tab)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 cursor-pointer whitespace-nowrap ${
                  activeSort === tab
                    ? "bg-gradient-to-r from-[#4cc9f0] to-[#f72585] text-white"
                    : "text-white/50 hover:text-white"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Duration Filter */}
          <div className="flex items-center gap-2 flex-wrap">
            {filterDurations.map((d) => (
              <button
                key={d}
                onClick={() => setSelectedDuration(d)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
                  selectedDuration === d
                    ? "bg-white/15 border-white/30 text-white"
                    : "border-white/10 text-white/40 hover:border-white/20 hover:text-white/60"
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>

        {/* Artist Filter Chips */}
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={() => setSelectedArtist("all")}
            className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
              selectedArtist === "all"
                ? "bg-white/10 border-white/30 text-white"
                : "border-white/10 text-white/40 hover:border-white/20 hover:text-white/60"
            }`}
          >
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-apps-line text-xs" />
            </div>
            전체
          </button>
          {artists.map((a) => (
            <button
              key={a.id}
              onClick={() => setSelectedArtist(a.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-full border text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                selectedArtist === a.id ? "text-white" : "border-white/10 text-white/40 hover:text-white/60"
              }`}
              style={
                selectedArtist === a.id
                  ? { background: `${a.accentColor}18`, borderColor: `${a.accentColor}60`, color: a.accentColor }
                  : {}
              }
            >
              <div className="w-5 h-5 rounded-full overflow-hidden flex-shrink-0">
                <img src={a.avatar} alt={a.name} className="w-full h-full object-cover" />
              </div>
              {a.name}
            </button>
          ))}
        </div>

        {/* Feed Grid */}
        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {filtered.map((item) => (
              <FeedCard key={item.id} item={item} onLike={() => toggleLike(item.id)} onBookmark={() => toggleBookmark(item.id)} />
            ))}
          </div>
        ) : (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <div className="w-16 h-16 flex items-center justify-center rounded-2xl bg-white/5 border border-white/10">
              <i className="ri-video-off-line text-3xl text-white/20" />
            </div>
            <p className="text-white/40 text-base">해당 아티스트의 작품이 없습니다</p>
            <button
              onClick={() => setSelectedArtist("all")}
              className="px-5 py-2 rounded-full border border-white/20 text-white/60 text-sm hover:border-white/40 hover:text-white/80 cursor-pointer transition-all whitespace-nowrap"
            >
              전체 보기
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function FeedCard({ item, onLike, onBookmark }: { item: FeedItem; onLike: () => void; onBookmark: () => void }) {
  const [showPlay, setShowPlay] = useState(false);
  const [showComment, setShowComment] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [comment, setComment] = useState("");
  const [shareCount, setShareCount] = useState(item.shares);
  const [copied, setCopied] = useState(false);

  const shareUrl = encodeURIComponent(window.location.href);
  const shareTitle = encodeURIComponent(item.title);

  const shareOptions = [
    {
      label: "YouTube",
      icon: "ri-youtube-line",
      color: "#FF0000",
      href: `https://www.youtube.com/upload`,
      isUpload: true,
    },
    {
      label: "Facebook",
      icon: "ri-facebook-circle-line",
      color: "#1877F2",
      href: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
    },
    {
      label: "TikTok",
      icon: "ri-tiktok-line",
      color: "#ffffff",
      href: `https://www.tiktok.com/upload`,
      isUpload: true,
    },
    {
      label: "Instagram",
      icon: "ri-instagram-line",
      color: "#E1306C",
      href: `https://www.instagram.com/`,
      isUpload: true,
    },
    {
      label: "Twitter/X",
      icon: "ri-twitter-x-line",
      color: "#ffffff",
      href: `https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareTitle}`,
    },
    {
      label: "링크 복사",
      icon: "ri-links-line",
      color: "#00f5d4",
      isCopy: true,
    },
  ];

  const handleShare = (opt: typeof shareOptions[0]) => {
    if (opt.isCopy) {
      navigator.clipboard.writeText(window.location.href).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
      });
    } else {
      window.open(opt.href, "_blank", "noopener,noreferrer");
    }
    setShareCount((prev) => prev + 1);
    setShowShare(false);
  };

  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur-sm overflow-hidden hover:border-white/20 transition-all duration-300 group">
      {/* User Info */}
      <div className="flex items-center justify-between px-4 pt-4 pb-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-full overflow-hidden border-2 border-[#4cc9f0]/40 flex-shrink-0">
            <img src={item.user.avatar} alt={item.user.name} className="w-full h-full object-cover" />
          </div>
          <div>
            <div className="text-white font-semibold text-sm">{item.user.name}</div>
            <div className="text-white/40 text-xs">{item.timeAgo}</div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-2 py-0.5 rounded-full bg-white/10 text-white/60 text-[10px] font-medium">{item.user.badge}</span>
        </div>
      </div>

      {/* Thumbnail */}
      <div
        className="relative mx-3 rounded-xl overflow-hidden cursor-pointer"
        style={{ aspectRatio: "16/9" }}
        onMouseEnter={() => setShowPlay(true)}
        onMouseLeave={() => setShowPlay(false)}
      >
        <img
          src={item.thumbnail}
          alt={item.title}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-black/20" />
        <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-200 ${showPlay ? "opacity-100" : "opacity-0"}`}>
          <div className="w-12 h-12 flex items-center justify-center rounded-full bg-white/20 backdrop-blur-sm border border-white/30">
            <i className="ri-play-fill text-white text-xl" />
          </div>
        </div>
        <div className="absolute bottom-2 left-2 px-2 py-0.5 rounded-md bg-black/70 backdrop-blur-sm">
          <span className="text-white text-xs font-mono">{item.duration}</span>
        </div>
        <div className="absolute bottom-2 right-2 px-2 py-0.5 rounded-md bg-[#f72585]/30 backdrop-blur-sm border border-[#f72585]/30">
          <span className="text-[#f72585] text-[10px] font-medium">{item.questTag}</span>
        </div>
      </div>

      {/* Content */}
      <div className="px-4 pt-3 pb-2">
        <h4 className="text-white font-semibold text-sm mb-1 line-clamp-1">{item.title}</h4>
        <p className="text-white/40 text-xs leading-relaxed line-clamp-2">{item.description}</p>
      </div>

      {/* Interaction Bar */}
      <div className="flex items-center justify-between px-4 py-3 border-t border-white/5">
        <div className="flex items-center gap-4">
          <button
            onClick={onLike}
            className={`flex items-center gap-1.5 text-xs transition-colors cursor-pointer ${item.liked ? "text-[#f72585]" : "text-white/40 hover:text-[#f72585]"}`}
          >
            <i className={`${item.liked ? "ri-heart-fill" : "ri-heart-line"} text-base`} />
            <span>{item.likes.toLocaleString()}</span>
          </button>
          <button
            onClick={() => setShowComment(!showComment)}
            className="flex items-center gap-1.5 text-xs text-white/40 hover:text-[#4cc9f0] transition-colors cursor-pointer"
          >
            <i className="ri-chat-1-line text-base" />
            <span>{item.comments}</span>
          </button>

          {/* Share Button with Dropdown */}
          <div className="relative">
            <button
              onClick={() => setShowShare(!showShare)}
              className={`flex items-center gap-1.5 text-xs transition-colors cursor-pointer ${showShare ? "text-[#00f5d4]" : "text-white/40 hover:text-[#00f5d4]"}`}
            >
              <i className="ri-share-forward-line text-base" />
              <span>{shareCount}</span>
            </button>

            {showShare && (
              <>
                {/* Backdrop */}
                <div
                  className="fixed inset-0 z-40"
                  onClick={() => setShowShare(false)}
                />
                {/* Dropdown */}
                <div className="absolute bottom-8 left-0 z-50 w-48 rounded-2xl border border-white/15 bg-[#12121a] backdrop-blur-xl shadow-2xl overflow-hidden">
                  <div className="px-4 py-3 border-b border-white/10">
                    <p className="text-white/60 text-xs font-medium">공유하기</p>
                  </div>
                  <div className="py-2">
                    {shareOptions.map((opt) => (
                      <button
                        key={opt.label}
                        onClick={() => handleShare(opt)}
                        className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 transition-colors cursor-pointer text-left"
                      >
                        <div
                          className="w-7 h-7 flex items-center justify-center rounded-lg flex-shrink-0"
                          style={{ background: `${opt.color}18`, border: `1px solid ${opt.color}30` }}
                        >
                          <i className={`${opt.icon} text-sm`} style={{ color: opt.color }} />
                        </div>
                        <span className="text-white/80 text-sm">
                          {opt.isCopy && copied ? "복사됨!" : opt.label}
                          {opt.isUpload && (
                            <span className="ml-1.5 text-[10px] text-white/30">업로드</span>
                          )}
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        <button
          onClick={onBookmark}
          className={`transition-colors cursor-pointer ${item.bookmarked ? "text-[#ffd60a]" : "text-white/40 hover:text-[#ffd60a]"}`}
        >
          <i className={`${item.bookmarked ? "ri-bookmark-fill" : "ri-bookmark-line"} text-base`} />
        </button>
      </div>

      {/* Comment Input */}
      {showComment && (
        <div className="px-4 pb-4 border-t border-white/5 pt-3">
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="댓글을 입력하세요..."
              className="flex-1 bg-white/5 border border-white/10 rounded-full px-4 py-2 text-xs text-white placeholder-white/30 outline-none focus:border-white/30 transition-colors"
            />
            <button
              onClick={() => setComment("")}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-gradient-to-r from-[#4cc9f0] to-[#f72585] text-white cursor-pointer flex-shrink-0"
            >
              <i className="ri-send-plane-fill text-xs" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
